import posthog from 'posthog-js';
import type { PostHogConfig } from 'posthog-js';

import { os } from '../../../lib/cep/node';
import { IS_PRODUCTION } from '../../../utils/currentEnv.utils';
import { APP_SLUG, getExchangeContext } from './context.utils';
import { ExchangeEvent, FailureStep } from './events';
import { resolveExchangeIdentity } from './identity.utils';

/**
 * Public project write key, hardcoded exactly like every other AutoCut client.
 * This is the **AutoCut V3** project — the same one the extension reported to
 * before the installer rewrite. It is deliberately NOT the desktop app's key:
 * the token is what selects the project, so anything sent with the desktop key
 * lands in the wrong dashboards.
 *
 * `photo.autocut.com` is our PostHog reverse proxy: an innocuous host survives
 * ad-blockers, and it answers CORS for the panel's `file://` origin (verified:
 * it reflects `Origin: null` and allows POST + content-type). The proxy is
 * token-agnostic and forwards to the same EU ingestion endpoint.
 */
const POSTHOG_API_KEY = 'phc_nkojbnXm618SkoNKJQ9dCeOqJpWmgosRbGbCBGKj9vN';
const POSTHOG_API_HOST = 'https://photo.autocut.com';
const POSTHOG_UI_HOST = 'https://eu.posthog.com';

const MAX_MESSAGE_LENGTH = 500;
const MAX_STACK_LENGTH = 1000;

type EventProperties = Record<string, unknown>;

/** One panel open = one session; correlates every event of a single run. */
const sessionId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
const sessionStartedAt = Date.now();

let isInitialized = false;
let hasReportedFailure = false;

export const getSessionElapsedMs = () => Date.now() - sessionStartedAt;

/**
 * Error messages routinely embed the installer path, which lives under
 * `os.tmpdir()` — on Windows that is `C:\Users\<name>\AppData\Local\Temp`, so a
 * raw message leaks the username. Scrub, flatten, truncate.
 */
const sanitizeText = (
  value: unknown,
  maxLength = MAX_MESSAGE_LENGTH
): string => {
  try {
    const text =
      value instanceof Error
        ? value.message
        : typeof value === 'string'
        ? value
        : JSON.stringify(value) ?? String(value);

    let sanitized = String(text).replace(/[\r\n]+/g, ' ');

    for (const secret of [
      (() => {
        try {
          return os.homedir();
        } catch {
          return '';
        }
      })(),
      (() => {
        try {
          return os.userInfo().username;
        } catch {
          return '';
        }
      })(),
    ]) {
      if (secret && secret.length > 2) {
        sanitized = sanitized.split(secret).join('~');
      }
    }

    return sanitized.slice(0, maxLength);
  } catch {
    return 'unserializable';
  }
};

type CaptureExtras = {
  /**
   * Send through `sendBeacon`, which the browser delivers independently of the
   * page. Required for anything captured right before `harakiri()` tears the
   * panel down and SIGKILLs the process.
   */
  useBeacon?: boolean;
  $set?: EventProperties;
  $set_once?: EventProperties;
};

/**
 * Fire-and-forget, deferred, and wrapped: capturing an event must never delay or
 * break the download, which is the panel's only job.
 */
export const captureExchangeEvent = (
  event: ExchangeEvent,
  properties: EventProperties = {},
  extras: CaptureExtras = {}
): void => {
  setTimeout(() => {
    try {
      if (!isInitialized) {
        return;
      }

      const payload: EventProperties = {
        ...getExchangeContext(),
        session_id: sessionId,
        elapsed_ms: getSessionElapsedMs(),
        ...properties,
      };

      if (!IS_PRODUCTION) {
        console.log('[PostHog]', event, payload);
      }

      posthog.capture(event, payload, {
        send_instantly: true,
        ...(extras.useBeacon ? { transport: 'sendBeacon' as const } : {}),
        ...(extras.$set ? { $set: extras.$set } : {}),
        ...(extras.$set_once ? { $set_once: extras.$set_once } : {}),
      });
    } catch (error) {
      console.warn('[analytics] Failed to capture event', event, error);
    }
  }, 0);
};

/**
 * At most one failure event per session: a single broken download otherwise
 * reports twice (rejected promise plus the global handler), which would make the
 * failure rate unreadable.
 */
export const captureExchangeFailureOnce = (
  step: FailureStep,
  error: unknown,
  properties: EventProperties = {}
): void => {
  try {
    if (hasReportedFailure) {
      return;
    }
    hasReportedFailure = true;

    const errorName =
      error instanceof Error
        ? error.name
        : typeof error === 'string'
        ? 'Error'
        : 'UnknownError';
    const stack = error instanceof Error ? error.stack : undefined;

    captureExchangeEvent(ExchangeEvent.Failed, {
      step,
      error_name: errorName,
      error_message: sanitizeText(error),
      ...(stack
        ? { error_stack_head: sanitizeText(stack, MAX_STACK_LENGTH) }
        : {}),
      ...properties,
    });
  } catch (captureError) {
    console.warn('[analytics] captureExchangeFailureOnce failed', captureError);
  }
};

export const hasCapturedFailure = () => hasReportedFailure;

/**
 * Give in-flight XHR sends a bounded moment before the caller destroys the page.
 * Events that must survive teardown should use `useBeacon` instead of relying on
 * this. Never rejects.
 */
export const flushExchangeEvents = (timeoutMs = 1000): Promise<void> =>
  new Promise<void>(resolve => {
    try {
      setTimeout(resolve, timeoutMs);
    } catch {
      resolve();
    }
  });

const buildPostHogOptions = (distinctId: string): Partial<PostHogConfig> => ({
  api_host: POSTHOG_API_HOST,
  ui_host: POSTHOG_UI_HOST,
  defaults: '2025-11-30',
  person_profiles: 'always',
  // The JSON stores are the source of truth for the distinct id (they are what
  // the desktop app reads), and the CEP cache that backs localStorage gets wiped.
  persistence: 'memory',
  // Anonymous, NOT identified. The desktop app reports to its own PostHog
  // project, so PostHog cannot merge the two persons for us — the shared
  // distinct id is what lets the migration funnel be joined across projects.
  bootstrap: { distinctID: distinctId, isIdentifiedID: false },
  // The panel SIGKILLs its own process seconds after the download; a batched
  // queue would simply be lost.
  request_batching: false,
  // A 20-second single-view installer panel needs none of this, and the desktop
  // app already disables most of it for event-volume reasons.
  autocapture: false,
  capture_pageview: false,
  capture_pageleave: false,
  capture_performance: false,
  disable_session_recording: true,
  disable_surveys: true,
  advanced_disable_feature_flags: true,
});

/**
 * Resolve identity, start PostHog, and emit the two "reach" events. Must be the
 * first thing `index.tsx` runs: i18n and `initBolt()` can both throw at module
 * scope, and a session we never hear about is worse than a broken one.
 */
export const bootstrapAnalytics = (): void => {
  try {
    if (isInitialized) {
      return;
    }

    const identity = resolveExchangeIdentity();
    posthog.init(POSTHOG_API_KEY, buildPostHogOptions(identity.distinctId));
    isInitialized = true;

    window.addEventListener('error', event => {
      captureExchangeFailureOnce(
        FailureStep.Unhandled,
        event.error ?? event.message
      );
    });
    window.addEventListener('unhandledrejection', event => {
      captureExchangeFailureOnce(FailureStep.Unhandled, event.reason);
    });

    const context = getExchangeContext();

    if (identity.isFirstOpen) {
      captureExchangeEvent(
        ExchangeEvent.FirstOpen,
        {},
        {
          $set_once: {
            came_from_ppro_exchange: true,
            exchange_first_seen_at: identity.firstSeenAt,
            exchange_first_host_app_version:
              context.host_app_version ?? 'unknown',
            exchange_first_os: context.os ?? 'UNKNOWN',
          },
        }
      );
    }

    captureExchangeEvent(
      ExchangeEvent.PanelOpened,
      {},
      {
        $set: {
          exchange_last_seen_at: new Date().toISOString(),
          exchange_extension_version: context.extension_version ?? 'unknown',
          exchange_os: context.os ?? 'UNKNOWN',
          exchange_host_app_version: context.host_app_version ?? 'unknown',
        },
        $set_once: { came_from_ppro_exchange: true },
      }
    );

    if (!IS_PRODUCTION) {
      console.log(`[PostHog] ${APP_SLUG} ready`, {
        distinctId: identity.distinctId,
        idSource: identity.idSource,
        isFirstOpen: identity.isFirstOpen,
        openCount: identity.openCount,
      });
    }
  } catch (error) {
    console.warn('[analytics] bootstrapAnalytics failed', error);
  }
};
